import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { Layout } from '@/layouts/Layout';
import { BankMethods } from '@/pages/Subscription/Checkout/BankMethods';
import { CardPayment } from '@/pages/Subscription/Checkout/CardPayment';
import { CheckoutDisclaimer } from '@/pages/Subscription/Checkout/CheckoutDisclaimer';
import { PlanSummaryCard } from '@/pages/Subscription/Checkout/PlanSummaryCard';
import { SmsPayment } from '@/pages/Subscription/Checkout/SmsPayment';
import { usePaymentOptions } from '@/pages/Subscription/hooks/usePaymentOptions';
import {
  SubscriptionMobileHeader,
  SubscriptionPageTitle,
} from '@/pages/Subscription/SubscriptionHeader';
import { ROUTE_SUBSCRIPTION } from '@/router/routes';
import {
  CheckoutDisclaimerType,
  PaymentMethod,
  PaymentOptionType,
} from '@/types/components';

export const SubscriptionCheckout: React.FC = () => {
  const { t } = useTranslation();
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();
  const { state } = useLocation() as {
    state: { type?: PaymentOptionType } | null;
  };
  const { data: paymentOptions, isLoading } = usePaymentOptions();

  const type = state?.type;
  const option = paymentOptions?.find(
    (paymentOption) => paymentOption.type === type,
  );
  const isSms = option?.type === PaymentOptionType.Sms;
  const hasBankMethods =
    option?.paymentMethods.some((method) => method !== PaymentMethod.Card) ??
    false;
  const disclaimerKey = hasBankMethods
    ? CheckoutDisclaimerType.Bank
    : CheckoutDisclaimerType.Card;

  if (!type || (!isLoading && !option)) {
    return <Navigate to={ROUTE_SUBSCRIPTION} replace />;
  }

  return (
    <Layout
      disallowPullToRefresh
      hideMobileNavbar
      mobileHeader={
        <SubscriptionMobileHeader
          title={t('subscription.checkout.title')}
          scrollProgress={scrollProgress}
        />
      }
      onScroll={handleScroll}
    >
      {isLoading && (
        <div className="flex min-h-[75vh] items-center justify-center">
          <PageSpinner />
        </div>
      )}

      {option && (
        <div className="flex flex-col gap-4 px-4 pb-8 md:px-6 md:pt-6">
          <SubscriptionPageTitle
            title={t('subscription.checkout.title')}
            scrollProgress={scrollProgress}
          />
          <div className="grid gap-3 md:max-w-[810px] md:grid-cols-2">
            <PlanSummaryCard option={option} />
            {isSms && <SmsPayment />}
            {!isSms && hasBankMethods && <BankMethods option={option} />}
            {!isSms && !hasBankMethods && <CardPayment option={option} />}
          </div>
          {!isSms && (
            <CheckoutDisclaimer
              heading={t(`subscription.checkout.${disclaimerKey}.heading`)}
              bullets={
                t(`subscription.checkout.${disclaimerKey}.bullets`, {
                  returnObjects: true,
                }) as string[]
              }
            />
          )}
        </div>
      )}
    </Layout>
  );
};
